import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const GDPRRequestForm = ({ currentLanguage }) => {
  const [formData, setFormData] = useState({
    requestType: '',
    fullName: '',
    email: '',
    phone: '',
    subject: '',
    description: '',
    dataCategories: [],
    verificationConsent: false,
    communicationConsent: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const requestTypes = [
    { value: 'access', label: 'Data Access Request', description: 'Request a copy of your personal data' },
    { value: 'rectification', label: 'Data Rectification', description: 'Request correction of inaccurate data' },
    { value: 'erasure', label: 'Data Erasure (Right to be Forgotten)', description: 'Request deletion of your data' },
    { value: 'portability', label: 'Data Portability', description: 'Request data in a structured format' },
    { value: 'restriction', label: 'Restriction of Processing', description: 'Limit how we process your data' },
    { value: 'objection', label: 'Object to Processing', description: 'Object to certain data processing activities' },
    { value: 'complaint', label: 'Privacy Complaint', description: 'Report a privacy concern or violation' }
  ];

  const dataCategories = [
    { value: 'personal', label: 'Personal Information', description: 'Name, address, contact details' },
    { value: 'financial', label: 'Financial Data', description: 'Investment records, transaction history' },
    { value: 'communication', label: 'Communication Records', description: 'Emails, chat logs, call records' },
    { value: 'technical', label: 'Technical Data', description: 'IP addresses, cookies, device information' },
    { value: 'marketing', label: 'Marketing Data', description: 'Preferences, campaign interactions' },
    { value: 'all', label: 'All Available Data', description: 'Complete data profile' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDataCategoryChange = (categoryValue, checked) => {
    setFormData(prev => ({
      ...prev,
      dataCategories: checked 
        ? [...prev?.dataCategories, categoryValue]
        : prev?.dataCategories?.filter(cat => cat !== categoryValue)
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  const getContent = () => {
    const content = {
      en: {
        title: 'GDPR Data Subject Rights Request',
        subtitle: 'Exercise your rights under the General Data Protection Regulation',
        processingTime: 'Processing Time: 30 days maximum',
        verificationNotice: 'Identity verification may be required for security purposes'
      },
      de: {
        title: 'DSGVO Betroffenenrechte Anfrage',
        subtitle: 'Machen Sie Ihre Rechte nach der Datenschutz-Grundverordnung geltend',
        processingTime: 'Bearbeitungszeit: Maximal 30 Tage',
        verificationNotice: 'Identitätsprüfung kann aus Sicherheitsgründen erforderlich sein'
      },
      fr: {
        title: 'Demande de Droits RGPD',
        subtitle: 'Exercez vos droits sous le Règlement Général sur la Protection des Données',
        processingTime: 'Temps de traitement: 30 jours maximum',
        verificationNotice: 'Une vérification d\'identité peut être requise pour des raisons de sécurité'
      },
      es: {
        title: 'Solicitud de Derechos RGPD',
        subtitle: 'Ejerce tus derechos bajo el Reglamento General de Protección de Datos',
        processingTime: 'Tiempo de procesamiento: 30 días máximo',
        verificationNotice: 'Puede requerirse verificación de identidad por razones de seguridad'
      }
    };
    return content?.[currentLanguage] || content?.en;
  };

  const content = getContent();

  if (isSubmitted) {
    return (
      <div className="bg-card border border-border rounded-brand p-8 text-center">
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="CheckCircle" size={32} className="text-success" />
        </div>
        <h3 className="text-xl font-semibold text-foreground mb-2">Request Submitted Successfully</h3>
        <p className="text-muted-foreground mb-4">
          Your GDPR request has been received and assigned reference number: <strong>GDPR-{Date.now()?.toString()?.slice(-6)}</strong>
        </p>
        <p className="text-sm text-muted-foreground mb-6">
          We will process your request within 30 days and contact you at the provided email address.
        </p>
        <Button
          variant="outline"
          onClick={() => {
            setIsSubmitted(false);
            setFormData({
              requestType: '',
              fullName: '',
              email: '',
              phone: '',
              subject: '',
              description: '',
              dataCategories: [],
              verificationConsent: false,
              communicationConsent: false
            });
          }}
        >
          Submit Another Request
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-brand p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-3 bg-primary/10 rounded-brand-sm">
          <Icon name="Shield" size={24} className="text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-foreground">{content?.title}</h2>
          <p className="text-sm text-muted-foreground">{content?.subtitle}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-muted/50 rounded-brand p-3 text-center">
          <Icon name="Clock" size={20} className="text-primary mx-auto mb-1" />
          <p className="text-xs font-medium text-foreground">{content?.processingTime}</p>
        </div>
        <div className="bg-muted/50 rounded-brand p-3 text-center">
          <Icon name="Shield" size={20} className="text-primary mx-auto mb-1" />
          <p className="text-xs font-medium text-foreground">Secure Processing</p>
        </div>
        <div className="bg-muted/50 rounded-brand p-3 text-center">
          <Icon name="Mail" size={20} className="text-primary mx-auto mb-1" />
          <p className="text-xs font-medium text-foreground">Email Confirmation</p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <Select
          label="Request Type"
          description="Select the type of GDPR request you want to make"
          required
          options={requestTypes}
          value={formData?.requestType}
          onChange={(value) => handleInputChange('requestType', value)}
          placeholder="Choose request type..."
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Full Name"
            type="text"
            required
            value={formData?.fullName}
            onChange={(e) => handleInputChange('fullName', e?.target?.value)}
            placeholder="Enter your full legal name"
          />
          <Input
            label="Email Address"
            type="email"
            required
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            placeholder="your.email@example.com"
          />
        </div>

        <Input
          label="Phone Number (Optional)"
          type="tel"
          value={formData?.phone}
          onChange={(e) => handleInputChange('phone', e?.target?.value)}
          placeholder="+49 40 123456789"
          description="For identity verification purposes if needed"
        />

        <Input
          label="Subject"
          type="text"
          required
          value={formData?.subject}
          onChange={(e) => handleInputChange('subject', e?.target?.value)}
          placeholder="Brief description of your request"
        />

        <div className="space-y-3">
          <label className="block text-sm font-medium text-foreground">
            Data Categories (Select all that apply)
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {dataCategories?.map((category) => (
              <div key={category?.value} className="border border-border rounded-brand p-3">
                <Checkbox
                  label={category?.label}
                  description={category?.description}
                  checked={formData?.dataCategories?.includes(category?.value)}
                  onChange={(e) => handleDataCategoryChange(category?.value, e?.target?.checked)}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            Detailed Description
          </label>
          <textarea
            className="w-full min-h-[120px] px-3 py-2 border border-border rounded-brand bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 resize-vertical"
            value={formData?.description}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
            placeholder="Please provide detailed information about your request, including any specific data or time periods you're interested in..."
            required
          />
        </div>

        <div className="space-y-4 p-4 bg-muted/30 rounded-brand">
          <Checkbox
            label="Identity Verification Consent"
            description={content?.verificationNotice}
            required
            checked={formData?.verificationConsent}
            onChange={(e) => handleInputChange('verificationConsent', e?.target?.checked)}
          />
          
          <Checkbox
            label="Communication Consent"
            description="I consent to receive updates about this request via email and phone"
            checked={formData?.communicationConsent}
            onChange={(e) => handleInputChange('communicationConsent', e?.target?.checked)}
          />
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={() => setFormData({
              requestType: '',
              fullName: '',
              email: '',
              phone: '',
              subject: '',
              description: '',
              dataCategories: [],
              verificationConsent: false,
              communicationConsent: false
            })}
          >
            Reset Form
          </Button>
          
          <Button
            type="submit"
            variant="default"
            className="flex-1 bg-gradient-to-r from-primary to-accent"
            loading={isSubmitting}
            disabled={!formData?.requestType || !formData?.fullName || !formData?.email || !formData?.verificationConsent}
          >
            {isSubmitting ? 'Submitting Request...' : 'Submit GDPR Request'}
          </Button>
        </div>
      </form>
      <div className="mt-6 p-4 bg-muted/50 rounded-brand">
        <div className="flex items-start space-x-2">
          <Icon name="Info" size={16} className="text-primary mt-0.5" />
          <div className="text-sm">
            <p className="text-foreground font-medium mb-1">Important Information</p>
            <ul className="text-muted-foreground space-y-1 text-xs">
              <li>• All requests are processed in accordance with GDPR Article 12-23</li>
              <li>• Identity verification may be required for security and privacy protection</li>
              <li>• Complex requests may require additional processing time (up to 60 days)</li>
              <li>• You will receive email confirmation within 72 hours of submission</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GDPRRequestForm;